import { useTheme } from '@emotion/react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import RebookingBikes from 'src/assets/png/rebookers-large-2.png';
import { ReactComponent as ControlArrow } from 'src/assets/svg/control-arrow.svg';

import { Button } from '../../../../components/button';
import { routes } from '../../../../constants/routes';
import { getTheme } from '../../../../utils/get-theme';
import RebookingSwapItHos from '../../assets/swap-it-black.png';

type RebookersSwapItLinkProps = {
    rebookingOpen: boolean;
    rebookingOpenDate: Date;
    keepItRoomUnavailable?: boolean;
};

export const RebookersSwapItLink: FC<RebookersSwapItLinkProps> = ({
    rebookingOpen,
    rebookingOpenDate,
    keepItRoomUnavailable = false,
}) => {
    const { t } = useTranslation();
    const { palette } = useTheme();
    const { isHosTheme } = getTheme();

    const date = rebookingOpenDate.getDate();
    const month = rebookingOpenDate.getMonth() + 1;
    const year = rebookingOpenDate.getFullYear();

    const formattedDate = `${date}-${month}-${year}`;

    if (keepItRoomUnavailable) {
        return (
            <Grid
                container
                alignItems="stretch"
                border={`1px solid ${palette.grey[300]}`}
                direction="row"
                mt={4}
                bgcolor={palette.grey[50]}
            >
                <Grid
                    item
                    lg={3}
                    component="img"
                    alt="Rebookers Info"
                    src={isHosTheme ? RebookingSwapItHos : RebookingBikes}
                    display={{
                        sm: 'none',
                        lg: 'block',
                    }}
                    css={{ objectFit: 'contain' }}
                />
                <Grid
                    item
                    sm={12}
                    md={12}
                    lg={9}
                    p={0}
                    justifyContent="space-between"
                    alignItems="center"
                    direction="row"
                >
                    <Stack height="100%" p={6} spacing={2} justifyContent="center">
                        <Typography variant="h2" display="inline-block" noWrap>
                            {t('rebooking_swap_it_card_title_keep_it_room_unavailable')}
                        </Typography>
                        <Typography variant="body1">{t('rebooking_swap_it_card_body')}</Typography>
                        <Box display="flex" alignItems="center">
                            <Button
                                href={routes.rebooking.swap}
                                variant="outlined"
                                css={{
                                    borderWidth: '2px',
                                    borderColor: 'black',
                                    ':hover': {
                                        borderColor: 'black',
                                    },
                                }}
                                endIcon={<ControlArrow css={{ transform: 'rotate(-90deg)' }} />}
                                disabled={!rebookingOpen}
                            >
                                {rebookingOpen ? (
                                    t('rebooking_swap_it_card_cta')
                                ) : (
                                    <Trans
                                        i18nKey="rebooking_keep_it_card_cta_price_released"
                                        values={{ date: formattedDate }}
                                    />
                                )}
                            </Button>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid
            container
            alignItems="stretch"
            justifyContent="space-between"
            border={`1px solid ${palette.grey[300]}`}
            direction="row"
            bgcolor={palette.grey[50]}
        >
            <Grid
                item
                xs={12}
                md={8}
                p={0}
                justifyContent="space-between"
                alignItems="center"
                direction="row"
            >
                <Grid container>
                    <Grid
                        item
                        xs={1}
                        md={isHosTheme ? 2 : 3}
                        component="img"
                        alt="Rebookers Info"
                        src={isHosTheme ? RebookingSwapItHos : RebookingBikes}
                        display={{
                            xs: 'none',
                            lg: 'block',
                        }}
                        css={{ objectFit: 'contain' }}
                        {...(isHosTheme && { mx: 1 })}
                    />
                    <Grid item display="flex" flexDirection="row" xs={12} lg={9}>
                        <Box p={4} display="flex" justifyContent="center" flexDirection="column">
                            <Typography variant="h2" display="inline-block" noWrap pb={2}>
                                {t('rebooking_swap_it_card_title')}
                            </Typography>
                            <Typography variant="body1">
                                {t('rebooking_swap_it_card_body')}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                pt={{ xs: 0, md: 4 }}
                pb={4}
                pr={4}
                pl={{ xs: 4, md: 0 }}
                display="flex"
                alignContent="center"
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            >
                <Box display="flex" alignItems="center">
                    <Button
                        href={routes.rebooking.swap}
                        variant="outlined"
                        css={{
                            borderWidth: '2px',
                            borderColor: 'black',
                            ':hover': {
                                borderColor: 'black',
                            },
                        }}
                        endIcon={<ControlArrow css={{ transform: 'rotate(-90deg)' }} />}
                        disabled={!rebookingOpen}
                    >
                        {rebookingOpen ? (
                            t('rebooking_swap_it_card_cta')
                        ) : (
                            <Trans
                                i18nKey="rebooking_keep_it_card_cta_price_released"
                                values={{ date: formattedDate }}
                            />
                        )}
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );
};
